import React, { useState, useEffect } from "react";
import "../css/payments.css";

function RegPayment() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [actionLoading, setActionLoading] = useState({});

  // Helper function for authenticated requests
  const authFetch = async (url, options = {}) => {
    const token = localStorage.getItem("access_token");
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers
    };
    return fetch(url, { ...options, headers });
  };

  useEffect(() => {
    const fetchPaymentData = async () => {
      setLoading(true);
      setError("");
      
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          setError("No authentication token found");
          setLoading(false);
          return;
        }

        const response = await authFetch("/api/admin/student_payments/register");
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to fetch registration payment data");
        }

        const data = await response.json();
        setPayments(data);

      } catch (error) {
        console.error("Error fetching registration payment data:", error);
        setError(error.message || "Failed to load registration payment data");
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentData();
  }, []);

  // Handle confirm payment
  const handleConfirmPayment = async (sip_id) => {
    setActionLoading(prev => ({ ...prev, [sip_id]: 'confirm' }));
    try {
      const response = await authFetch(`/api/admin/student_payments/register/${sip_id}`, {
        method: "POST"
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to confirm registration payment");
      }

      // Update local state
      setPayments(prev => prev.map(p => 
        p.sip_id === sip_id ? { ...p, reg_payment_conf: true } : p
      ));

    } catch (error) {
      console.error("Error confirming payment:", error);
      alert(error.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [sip_id]: null }));
    }
  };

  // Handle unconfirm payment
  const handleUnconfirmPayment = async (sip_id) => {
    setActionLoading(prev => ({ ...prev, [sip_id]: 'unconfirm' }));
    try {
      const response = await authFetch(`/api/admin/student_payments/register/${sip_id}`, {
        method: "DELETE"
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to unconfirm registration payment");
      }

      // Update local state
      setPayments(prev => prev.map(p => 
        p.sip_id === sip_id ? { ...p, reg_payment_conf: false } : p
      ));

    } catch (error) {
      console.error("Error unconfirming payment:", error);
      alert(error.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [sip_id]: null }));
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge class
  const getStatusBadge = (status) => {
    if (!status) return "status-badge pending";
    switch(status.toLowerCase()) {
      case 'success':
        return "status-badge success";
      case 'failed':
        return "status-badge failed";
      case 'pending':
        return "status-badge pending";
      default:
        return "status-badge pending";
    }
  };

  // Get confirmation badge
  const getConfirmationBadge = (value) => {
    if (value === true || value === 1 || value === "true") {
      return <span className="confirmation-badge confirmed">✓ Confirmed</span>;
    } else if (value === false || value === 0 || value === "false") {
      return <span className="confirmation-badge not-confirmed">✗ Not Confirmed</span>;
    } else {
      return <span className="confirmation-badge pending">—</span>;
    }
  };

  // Apply search filter
  const getFilteredPayments = () => {
    if (!searchTerm) return payments;

    return payments.filter(payment => 
      payment.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.sip_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredPayments = getFilteredPayments();

  if (loading) {
    return (
      <div className="payment-stats loading">
        <h2>Registration Payments Overview</h2>
        <div className="loading-spinner">Loading registration payment data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-stats error">
        <h2>Registration Payments Overview</h2>
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={() => window.location.reload()} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-stats full-screen">
      <div className="header-section">
        <h2>Registration Payments Overview</h2>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by name, SIP ID, email, or transaction ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>
      </div>

      {/* Summary Stats */}
      {/* <div className="summary-section">
        <div className="stat-item">
          <span className="stat-label">Total Students:</span>
          <span className="stat-value">{payments.length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Confirmed:</span>
          <span className="stat-value success">{payments.filter(p => p.reg_payment_conf === true).length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Not Confirmed:</span>
          <span className="stat-value warning">{payments.filter(p => p.reg_payment_conf === false).length}</span>
        </div>
        {searchTerm && (
          <div className="stat-item">
            <span className="stat-label">Showing:</span>
            <span className="stat-value info">{filteredPayments.length}</span>
          </div>
        )}
      </div> */}

      {/* Students Table */}
      {filteredPayments.length > 0 ? (
        <div className="table-container">
          <table className="students-table">
            <thead>
              <tr>
                <th>SIP ID</th>
                <th>Student Name</th>
                <th>Email</th>
                <th>Transaction ID</th>
                <th>Amount</th>
                <th>Payment Status</th>
                <th>Payment Date</th>
                <th>Registration Payment</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment, index) => (
                <tr key={payment.sip_id || index}>
                  <td>
                    <span className="sip-id-badge">{payment.sip_id || "N/A"}</span>
                  </td>
                  <td>
                    <strong>{payment.name || "N/A"}</strong>
                  </td>
                  <td>
                    <a href={`mailto:${payment.email}`} className="email-link">
                      {payment.email || "N/A"}
                    </a>
                  </td>
                  <td>
                    {payment.transaction_id ? (
                      <span className="transaction-id" title={payment.transaction_id}>
                        {payment.transaction_id}
                      </span>
                    ) : (
                      <span className="no-data">—</span>
                    )}
                  </td>
                  <td>
                    {payment.amount ? (
                      <span className="amount">{formatCurrency(payment.amount)}</span>
                    ) : (
                      <span className="no-data">—</span>
                    )}
                  </td>
                  <td>
                    {payment.status ? (
                      <span className={getStatusBadge(payment.status)}>
                        {payment.status}
                      </span>
                    ) : (
                      <span className="no-data">—</span>
                    )}
                  </td>
                  <td>
                    {payment.order_date ? formatDate(payment.order_date) : "—"}
                  </td>
                  <td className="confirmation-cell">
                    <div className="confirmation-with-action">
                      {getConfirmationBadge(payment.reg_payment_conf)}
                      {payment.reg_payment_conf ? (
                        <button
                          onClick={() => handleUnconfirmPayment(payment.sip_id)}
                          disabled={actionLoading[payment.sip_id] === 'unconfirm'}
                          className="small-action-btn unconfirm-btn"
                          title="Unconfirm Registration Payment"
                        >
                          {actionLoading[payment.sip_id] === 'unconfirm' ? (
                            <span className="loading-spinner-small"></span>
                          ) : (
                            'Undo'
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleConfirmPayment(payment.sip_id)}
                          disabled={actionLoading[payment.sip_id] === 'confirm'}
                          className="small-action-btn confirm-btn"
                          title="Confirm Registration Payment"
                        >
                          {actionLoading[payment.sip_id] === 'confirm' ? (
                            <span className="loading-spinner-small"></span>
                          ) : (
                            'Confirm'
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">
          {searchTerm ? (
            <p>No students found matching your search criteria.</p>
          ) : (
            <p>No registration payment data found.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default RegPayment;